import { TPASensorReading, TPAStage } from '../models/tpa-test-data.modal';
import { DataFit, dataFitting } from './data-fitting.utils';

export const MIN_FIT_SAMPLES = 6;

export interface StageAnalysis extends DataFit {
  sampleCount: number;
  power: number;
  t1: number;
  t2: number;
  tr: number;
}

export function toInt32(value: number): number {
  const floor = Math.floor(value);
  const fraction = value - floor;
  if (fraction > 0.5) return floor + 1;
  if (fraction < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

export function truncateValueSingle(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  const scaled = value * factor;
  const low = Math.floor(scaled);
  const diff = scaled - low;

  let rounded: number;
  if (diff > 0.5) rounded = low + 1;
  else if (diff < 0.5) rounded = low;
  else rounded = low % 2 === 0 ? low : low + 1; // half to even

  return Math.fround(rounded / factor);
}

export function analyseTimeRange(
  readings: TPASensorReading[],
  rangeStartTime: number,
  rangeEndTime: number,
): StageAnalysis | null {
  const startTime = toInt32(rangeStartTime);
  const endTime = toInt32(rangeEndTime);
  let power = 0;
  const xValues: number[] = [];
  const yValues: number[] = [];

  for (let i = 0; i < readings.length; i += 1) {
    const reading = readings[i];
    if (reading.time >= startTime && reading.time <= endTime) {
      xValues.push(reading.time);
      yValues.push(truncateValueSingle(reading.temperature, 2));
      power = Math.fround(reading.power);
    }
    if (reading.time > endTime) break;
  }

  if (readings.length === 0) return null;

  const logXValues = xValues.map((x) => Math.fround(Math.log(x)));
  const fit = dataFitting(logXValues, yValues);
  const t1 = fit.slope * Math.log(1000) + fit.intercept;
  const t2 = fit.slope * Math.log(100) + fit.intercept;
  const tr = Math.fround((4 * Math.PI * (t1 - t2)) / (2.303 * power));

  return {
    ...fit,
    sampleCount: xValues.length,
    power,
    t1,
    t2,
    tr,
  };
}

export function analyseStage(readings: TPASensorReading[], stage: TPAStage): StageAnalysis | null {
  return analyseTimeRange(readings, stage.startTime, stage.endTime);
}
